import React, { useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EmailBlockData } from './ModernEmailBuilder';

// Human-readable label for each block type
const BLOCK_LABELS: Record<string, string> = {
  text: 'Text',
  heading: 'Heading',
  title: 'Title',
  subtitle: 'Subtitle',
  quote: 'Quote',
  caption: 'Caption',
  preheader: 'Preheader',
  list: 'List',
  'magic-text': 'AI Text',
  image: 'Image',
  video: 'Video',
  giphy: 'GIF',
  sticker: 'Sticker',
  'image-carousel': 'Image Carousel',
  'qr-code': 'QR Code',
  button: 'Button',
  social: 'Social Links',
  poll: 'Poll',
  survey: 'Survey',
  'star-rating': 'Star Rating',
  countdown: 'Countdown',
  accordion: 'Accordion',
  divider: 'Divider',
  spacer: 'Spacer',
  columns: 'Columns',
  header: 'Header',
  footer: 'Footer',
  hero: 'Hero',
  logo: 'Logo',
  menu: 'Menu',
  'hero-content': 'Hero Section',
  'image-content': 'Image + Content',
  'feature-showcase': 'Feature Showcase',
  'cta-banner': 'CTA Banner',
  'promo-banner': 'Promo Banner',
  'features-grid': 'Features Grid',
  'blog-preview': 'Blog Preview',
  'faq-section': 'FAQ Section',
  'team-grid': 'Team Grid',
  'pricing-table': 'Pricing Table',
  testimonial: 'Testimonials',
  'profile-card': 'Profile Card',
  'event-announcement': 'Event',
  'music-launch': 'Music Launch',
  newsletter: 'Newsletter',
  product: 'Product',
  'product-rec': 'Products',
  code: 'HTML Code',
};

function getBlockLabel(block: EmailBlockData): string {
  // Try to get a meaningful name from content
  if (block.content?.text && typeof block.content.text === 'string') {
    const truncated = block.content.text.slice(0, 28);
    return truncated.length < block.content.text.length ? `${truncated}…` : truncated;
  }
  if (block.content?.heading && typeof block.content.heading === 'string') {
    return block.content.heading.slice(0, 28);
  }
  if (block.content?.title && typeof block.content.title === 'string') {
    return block.content.title.slice(0, 28);
  }
  if (block.content?.name && typeof block.content.name === 'string') {
    return block.content.name.slice(0, 28);
  }
  return BLOCK_LABELS[block.type] ?? block.type;
}

// Block type → icon
function BlockIcon({ type }: { type: string }) {
  const icons: Record<string, React.ReactNode> = {
    text: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h10M4 18h10" /></svg>,
    heading: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h2m0 0v12m0-12h8m0 0v12m0-12h2M4 12h8" /></svg>,
    image: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 19.5h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5z" /></svg>,
    button: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><rect x="3" y="8" width="18" height="8" rx="4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    divider: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18" /></svg>,
    spacer: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" /></svg>,
    social: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" /></svg>,
    columns: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125z" /></svg>,
    video: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" /></svg>,
    footer: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h9.75m4.5-4.5v12m0 0l-3.75-3.75M17.25 21l3.75-3.75" /></svg>,
    header: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 01-1.125-1.125v-3.75zm0 9.75C2.25 16.254 2.754 16.5 3.375 16.5h6c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125h-6A1.125 1.125 0 012.25 18v-1.5zm9.75-9.75c0-.621.504-1.125 1.125-1.125h8.25c.621 0 1.125.504 1.125 1.125v9c0 .621-.504 1.125-1.125 1.125h-8.25A1.125 1.125 0 0112 16.875v-9z" /></svg>,
  };
  const defaultIcon = <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6z" /></svg>;
  return <>{icons[type] ?? defaultIcon}</>;
}

interface LayerRowProps {
  block: EmailBlockData;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

const LayerRow: React.FC<LayerRowProps> = ({ block, isSelected, onSelect, onDelete, onDuplicate }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const [menuOpen, setMenuOpen] = useState(false);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all ${
        isSelected
          ? 'bg-indigo-50 text-indigo-700'
          : 'hover:bg-black/[0.03] text-slate-700'
      }`}
      onClick={onSelect}
    >
      {/* Drag handle */}
      <span
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-3 h-5" fill="currentColor" viewBox="0 0 6 14">
          <circle cx="1.5" cy="2" r="1.5" />
          <circle cx="4.5" cy="2" r="1.5" />
          <circle cx="1.5" cy="7" r="1.5" />
          <circle cx="4.5" cy="7" r="1.5" />
          <circle cx="1.5" cy="12" r="1.5" />
          <circle cx="4.5" cy="12" r="1.5" />
        </svg>
      </span>

      {/* Icon */}
      <span className={`flex-shrink-0 ${isSelected ? 'text-indigo-500' : 'text-slate-400'}`}>
        <BlockIcon type={block.type} />
      </span>

      {/* Label */}
      <span className="flex-1 min-w-0 text-[12px] font-medium truncate">
        {getBlockLabel(block)}
      </span>

      {/* Type badge */}
      <span className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-wide text-slate-300 hidden group-hover:inline">
        {BLOCK_LABELS[block.type] ?? block.type}
      </span>

      {/* ··· menu */}
      <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => setMenuOpen(v => !v)}
          className="flex items-center justify-center w-5 h-5 rounded text-slate-300 hover:text-slate-600 hover:bg-black/[0.06] transition-colors opacity-0 group-hover:opacity-100"
        >
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 14a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" />
          </svg>
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-0 top-6 z-20 w-36 bg-white rounded-xl shadow-xl border border-black/[0.07] py-1 animate-in fade-in slide-in-from-top-1 duration-150">
              <button type="button" onClick={() => { onDuplicate(); setMenuOpen(false); }}
                className="w-full text-left px-3 py-2 text-[12px] text-slate-600 hover:bg-slate-50 flex items-center gap-2">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" /></svg>
                Duplicate
              </button>
              <button type="button" onClick={() => { onDelete(); setMenuOpen(false); }}
                className="w-full text-left px-3 py-2 text-[12px] text-red-500 hover:bg-red-50 flex items-center gap-2">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface LayersPanelProps {
  blocks: EmailBlockData[];
  selectedBlockId: string | null;
  onSelectBlock: (id: string) => void;
  onReorderBlocks: (blocks: EmailBlockData[]) => void;
  onDeleteBlock: (id: string) => void;
  onDuplicateBlock: (id: string) => void;
  onClose: () => void;
}

export const LayersPanel: React.FC<LayersPanelProps> = ({
  blocks,
  selectedBlockId,
  onSelectBlock,
  onReorderBlocks,
  onDeleteBlock,
  onDuplicateBlock,
  onClose,
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = blocks.findIndex(b => b.id === active.id);
      const newIndex = blocks.findIndex(b => b.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderBlocks(arrayMove(blocks, oldIndex, newIndex));
      }
    }
  }, [blocks, onReorderBlocks]);

  return (
    <div
      className="absolute bottom-[52px] left-3 z-[300] w-[260px] rounded-2xl bg-white shadow-2xl border border-black/[0.07] flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-200"
      style={{ maxHeight: 'calc(100vh - 200px)' }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/[0.06] flex-shrink-0">
        <h3 className="text-[13px] font-semibold text-slate-800">Manage Layers</h3>
        <button
          type="button"
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-black/[0.06] transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Block list */}
      <div className="flex-1 overflow-y-auto no-scrollbar hide-scrollbar p-2">
        {blocks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-slate-400">
            <svg className="w-8 h-8 mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
            </svg>
            <p className="text-[11px]">No blocks yet</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
              {blocks.map((block) => (
                <LayerRow
                  key={block.id}
                  block={block}
                  isSelected={selectedBlockId === block.id}
                  onSelect={() => onSelectBlock(block.id)}
                  onDelete={() => onDeleteBlock(block.id)}
                  onDuplicate={() => onDuplicateBlock(block.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Footer count */}
      <div className="px-4 py-2 border-t border-black/[0.05] flex-shrink-0">
        <p className="text-[10px] text-slate-400">{blocks.length} block{blocks.length !== 1 ? 's' : ''}</p>
      </div>
    </div>
  );
};
